import { TVideoSource } from '@/modules/content/branded/types';
import { AspectRatio, Box, Image, Text } from '@chakra-ui/react';
import MuxPlayer from '@mux/mux-player-react';
import React from 'react';

type Props = {
  source: TVideoSource;
  youtubeVideoId?: string | null;
  muxStatus?: string | null;
  muxPlaybackId?: string | null;
  posterUrl?: string | null;
  title?: string;
  // A freshly-picked file not yet uploaded/processed — never play a stale asset.
  pendingUpload?: boolean;
  // Shown when source is youtube but no valid video id is available yet.
  emptyHint?: string;
};

const StatusPoster = ({ posterUrl, label }: { posterUrl?: string | null; label: string }) => (
  <Box position="relative" width="100%" borderRadius="12px" overflow="hidden" backgroundColor="background.darkGray">
    {posterUrl ? (
      <Image src={posterUrl} alt="Video thumbnail" width="100%" maxHeight="360px" objectFit="cover" />
    ) : (
      <AspectRatio ratio={16 / 9} width="100%">
        <Box />
      </AspectRatio>
    )}
    <Text
      position="absolute"
      bottom="12px"
      left="12px"
      fontSize="11px"
      fontWeight={700}
      textTransform="uppercase"
      color="white"
      bgColor="blackAlpha.600"
      borderRadius="full"
      paddingX="10px"
      paddingY="3px"
    >
      {label}
    </Text>
  </Box>
);

// Shared video renderer: plays YouTube (embed) and ready Mux assets (Mux player),
// and falls back to a poster + status while a Mux asset is still processing.
const VideoPlayer = ({
  source,
  youtubeVideoId,
  muxStatus,
  muxPlaybackId,
  posterUrl,
  title,
  pendingUpload,
  emptyHint,
}: Props) => {
  if (source === 'youtube') {
    if (!youtubeVideoId) {
      return (
        <Text color="text.mediumGray" fontSize="14px">
          {emptyHint ?? 'No video yet.'}
        </Text>
      );
    }
    return (
      <AspectRatio ratio={16 / 9} width="100%" borderRadius="12px" overflow="hidden">
        <Box as="iframe" src={`https://www.youtube.com/embed/${youtubeVideoId}`} allowFullScreen />
      </AspectRatio>
    );
  }

  if (!pendingUpload && muxStatus === 'ready' && muxPlaybackId) {
    return (
      <Box width="100%" borderRadius="12px" overflow="hidden">
        <MuxPlayer
          playbackId={muxPlaybackId}
          streamType="on-demand"
          accentColor="#4E1AEF"
          metadata={{ video_title: title }}
          style={{ width: '100%', aspectRatio: '16 / 9' }}
        />
      </Box>
    );
  }

  const label = pendingUpload
    ? 'New video — available after processing'
    : muxStatus === 'ready'
      ? 'Mux video'
      : `Mux video · ${muxStatus ?? 'processing'}`;
  return <StatusPoster posterUrl={posterUrl} label={label} />;
};

export default VideoPlayer;
